import React, { useContext } from 'react';
import { StateContext } from '../../StateContext';
import { secondsToHours, totalHours, totalProjectHours } from '../Links/weekView';
import './hourOverviewPage.css';
import { useLanguage } from '../../Services/LanguageContext';

interface IProps {
    year: number;
}

const YearOverviewPage = React.forwardRef<HTMLDivElement, IProps>((props, ref) => {
    const { t } = useLanguage();
    const { loginData } = useContext(StateContext);
    const { year } = props;

    if (!loginData?.mechanics || loginData.mechanics.length === 0) {
        return (
            <div ref={ref} className='print-page'>
                <div className='print-header'>
                    <h1 className='print-title'>{t('noDataAvailable')}</h1>
                </div>
            </div>
        );
    }

    const getYearFromWeekData = (wd: any) =>
        wd.year || new Date(wd.created).getFullYear();

    const projectsInYear = (loginData.projects ?? []).filter(project =>
        (project.links ?? []).some(link =>
            (link.weekData ?? []).some(wd => getYearFromWeekData(wd) === year)
        )
    );

    // Year-filtered hours per mechanic (for dominant mechanic attribution only).
    // Project result uses all-time hours + type-based estimate, matching the per-project badge formula.
    const projectStats = projectsInYear.map(project => {
        const hoursPerMechanic: Record<number, number> = {};
        let projectYearTotal = 0;
        for (const link of project.links ?? []) {
            const yearData = (link.weekData ?? []).filter(wd => getYearFromWeekData(wd) === year);
            const h = totalHours(yearData);
            if (h > 0) {
                hoursPerMechanic[link.mechanicId] = (hoursPerMechanic[link.mechanicId] ?? 0) + h;
                projectYearTotal += h;
            }
        }
        const hasTypes = (project.types ?? []).length > 0;
        const estimated = hasTypes
            ? (project.types ?? []).reduce((sum, pt) => sum + secondsToHours(pt.calculatedTimeInSeconds), 0)
            : secondsToHours(project.estimatedTimeInSeconds ?? 0);
        const allTimeHours = totalProjectHours(project.links ?? []);
        const projectResult = estimated - allTimeHours;
        return { project, projectTotal: projectYearTotal, hoursPerMechanic, projectResult };
    });

    // Assign each project to the mechanic with >50% of its hours (if any)
    const mechanicResultMap: Record<number, number> = {};
    const mechanicMeerwerkMap: Record<number, number> = {};
    const mechanicDhzMap: Record<number, number> = {};
    for (const { project, projectTotal, hoursPerMechanic, projectResult } of projectStats) {
        if (projectTotal === 0) continue;
        const dominantMechanicId = Object.entries(hoursPerMechanic)
            .reduce<[string, number]>((max, [id, h]) => h > max[1] ? [id, h] : max, ['', 0])[0];
        if (dominantMechanicId) {
            const mechanicId = Number(dominantMechanicId);
            mechanicResultMap[mechanicId] = (mechanicResultMap[mechanicId] ?? 0) + projectResult;
            mechanicMeerwerkMap[mechanicId] = (mechanicMeerwerkMap[mechanicId] ?? 0) + secondsToHours(project.meerwerkSeconds ?? 0);
            mechanicDhzMap[mechanicId] = (mechanicDhzMap[mechanicId] ?? 0) + secondsToHours(project.dhzSeconds ?? 0);
        }
    }

    const mechanicTotals = (loginData.mechanics ?? [])
        .map(mechanic => {
            const hours = (mechanic.links ?? []).reduce((total, link) => {
                const yearData = (link.weekData ?? []).filter(
                    wd => getYearFromWeekData(wd) === year
                );
                return total + totalHours(yearData);
            }, 0);
            const mechanicResult = mechanicResultMap[mechanic.id] ?? null;
            const mechanicMeerwerk = mechanicMeerwerkMap[mechanic.id] ?? null;
            const mechanicDhz = mechanicDhzMap[mechanic.id] ?? null;
            return { name: mechanic.name, hours, mechanicResult, mechanicMeerwerk, mechanicDhz };
        })
        .filter(m => m.hours > 0)
        .sort((a, b) => b.hours - a.hours);

    const totalMeerwerk = projectsInYear.reduce(
        (sum, p) => sum + secondsToHours(p.meerwerkSeconds ?? 0), 0
    );
    const totalDhz = projectsInYear.reduce(
        (sum, p) => sum + secondsToHours(p.dhzSeconds ?? 0), 0
    );
    const totalEstimated = projectsInYear.reduce((sum, p) => {
        const hasTypes = (p.types ?? []).length > 0;
        return sum + (hasTypes
            ? (p.types ?? []).reduce((s, pt) => s + secondsToHours(pt.calculatedTimeInSeconds), 0)
            : secondsToHours(p.estimatedTimeInSeconds ?? 0));
    }, 0);
    const grandTotal = mechanicTotals.reduce((sum, m) => sum + m.hours, 0);
    const result = projectStats.reduce((sum, { projectResult }) => sum + projectResult, 0);

    return (
        <div ref={ref} className='print-page'>
            <div className='print-header'>
                <h1 className='print-title'>Jaar Overzicht {year}</h1>
                <div className='print-project-code'>{t('hoursOverview')}</div>
            </div>

            <table className='print-table'>
                <thead>
                    <tr>
                        <th className='print-th' style={{ textAlign: 'left', paddingLeft: '8px' }}>
                            {t('mechanic')}
                        </th>
                        <th className='print-th print-total-col'>{t('hours')}</th>
                        <th className='print-th print-total-col'>{t('result')}</th>
                        <th className='print-th print-total-col'>{t('meerwerk')}</th>
                        <th className='print-th print-total-col'>{t('dhzWork')}</th>
                    </tr>
                </thead>
                <tbody>
                    {mechanicTotals.map((m, i) => (
                        <tr key={i}>
                            <td className='print-td print-mechanic-name-col'>{m.name}</td>
                            <td className='print-td print-total-cell'>{m.hours.toFixed(2)}</td>
                            <td className={`print-td print-total-cell ${m.mechanicResult === null ? '' : m.mechanicResult >= 0 ? 'positive' : 'negative'}`}>
                                {m.mechanicResult === null ? '—' : `${m.mechanicResult >= 0 ? '+' : ''}${m.mechanicResult.toFixed(2)}`}
                            </td>
                            <td className='print-td print-total-cell' style={{ color: '#1976d2' }}>
                                {m.mechanicMeerwerk === null || m.mechanicMeerwerk === 0 ? '—' : `+${m.mechanicMeerwerk.toFixed(2)}`}
                            </td>
                            <td className='print-td print-total-cell' style={{ color: '#c62828' }}>
                                {m.mechanicDhz === null || m.mechanicDhz === 0 ? '—' : `+${m.mechanicDhz.toFixed(2)}`}
                            </td>
                        </tr>
                    ))}

                    <tr className='print-totals-row'>
                        <td className='print-td' style={{ textAlign: 'left', paddingLeft: '8px', fontWeight: 'bold' }}>
                            {t('total')}
                        </td>
                        <td className='print-td print-grand-total'>{grandTotal.toFixed(2)}</td>
                        <td className={`print-td print-grand-total ${result >= 0 ? 'positive' : 'negative'}`}>
                            {result >= 0 ? '+' : ''}{result.toFixed(2)}
                        </td>
                        <td className='print-td print-grand-total' style={{ color: '#1976d2' }}>
                            {totalMeerwerk > 0 ? `+${totalMeerwerk.toFixed(2)}` : '—'}
                        </td>
                        <td className='print-td print-grand-total' style={{ color: '#c62828' }}>
                            {totalDhz > 0 ? `+${totalDhz.toFixed(2)}` : '—'}
                        </td>
                    </tr>
                </tbody>
            </table>

            <div className='print-summary'>
                <div className='print-summary-row'>
                    <span className='print-summary-label'>{t('hoursWorked')}:</span>
                    <span className='print-summary-value'>{grandTotal.toFixed(2)}</span>
                </div>
<div className='print-summary-row'>
                    <span className='print-summary-label'>{t('hoursEstimated')}:</span>
                    <span className='print-summary-value'>{totalEstimated.toFixed(2)}</span>
                </div>
                <div className='print-summary-row print-summary-result'>
                    <span className='print-summary-label'>{t('result')}:</span>
                    <span className={`print-summary-value ${result >= 0 ? 'positive' : 'negative'}`}>
                        {result >= 0 ? '+' : ''}{result.toFixed(2)}
                    </span>
                </div>
            </div>
        </div>
    );
});

export default YearOverviewPage;
